import { action } from '@ember/object';
import type Owner from '@ember/owner';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import { YAxisScaleType } from 'webapp/enums/enums';

import didUpdate from 'webapp/modifiers/did-update';
import FormCheckboxSwitchComponent from 'webapp/components/form/checkbox-switch';
import FormInputComponent from 'webapp/components/form/input';
import FormRadiobuttonGroupComponent from 'webapp/components/form/radiobutton-group';
import FormSelectComponent from 'webapp/components/form/select';

import type Block from 'webapp/models-custom/block';
import type BlockSetup from 'webapp/models-custom/block-setup';
import type VisualizationBarChart from 'webapp/models-custom/visualization/bar-chart';
import type VisualizationLineChart from 'webapp/models-custom/visualization/line-chart';
import type { YAxisScale, YAxisScaleNotOff, YAxisScaleOff } from 'webapp/models/block';

interface Args {
	blockM: Block;
	scaleIndex: 0 | 1;
}

export type bz = number;

export default class BlockSettingsYAxisComponent extends Component<Args> {
	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.updateThisFromModel();
	}

	scaleTypeOptions = [
		{
			value: YAxisScaleType.OFF,
			label: 'Off',
		},
		{
			value: YAxisScaleType.AUTO,
			label: 'Auto',
		},
		{
			value: YAxisScaleType.CUSTOM,
			label: 'Manual',
		},
	];

	get block(): Block {
		return this.args.blockM;
	}

	get blockSetup(): BlockSetup {
		return this.block.blockSetup;
	}

	get visualization(): VisualizationLineChart | VisualizationBarChart {
		return this.blockSetup.visualization as VisualizationLineChart | VisualizationBarChart;
	}

	get isSecondaryYAxis(): boolean {
		return this.args.scaleIndex === 1;
	}

	get showScalingTypeSelector(): boolean {
		return !this.isSecondaryYAxis || this.isSecondaryAxisOn;
	}

	get showReverseToggle(): boolean {
		return !this.isSecondaryYAxis || this.isSecondaryAxisOn;
	}

	get yAxisScaleSettings(): YAxisScale | undefined {
		return this.block.yAxisScale[this.args.scaleIndex];
	}

	get selectedScaleType(): YAxisScaleType {
		// default is AUTO
		const settings = this.yAxisScaleSettings;
		if ((settings as YAxisScaleOff | undefined)?.disabled) {
			return YAxisScaleType.OFF;
		} else if (
			!settings ||
			((settings as YAxisScaleNotOff).min === null &&
				(settings as YAxisScaleNotOff).max === null)
		) {
			return YAxisScaleType.AUTO;
		} else {
			return YAxisScaleType.CUSTOM;
		}
	}

	@action
	changeScaleType(value: AnySimpleValue): void {
		const scaleType = value as YAxisScaleType;
		const isCustom = scaleType === YAxisScaleType.CUSTOM;
		this.showValueInputs = isCustom;
		if (isCustom) {
			if (this.minValue === null && this.maxValue === null) {
				this.minValue = 0; // so it does not count as "auto" anymore
			}
		}
		const scaleTextForTracking =
			scaleType === YAxisScaleType.OFF ? 'off' : isCustom ? 'custom' : 'auto';
		this.blockSetup.trackBlockPropertySettingsChangeEvent({
			'y-axis_scaling': scaleTextForTracking,
		});
		this.updateModelFromThis(scaleType);
	}

	// ----- manual min / max -----

	@tracked minValue: number | null = null;
	@tracked maxValue: number | null = null;
	@tracked showValueInputs = false;

	@action
	changeMinValue(): void {
		const value = this.minValue;
		let numericValue: number | null = null;
		if (value && !isNaN(+value)) {
			numericValue = +value;
		}
		this.minValue = numericValue;
		this.updateModelFromThis();
	}

	@action
	changeMaxValue(): void {
		const value = this.maxValue;
		let numericValue: number | null = null;
		if (value && !isNaN(+value)) {
			numericValue = +value;
		}
		this.maxValue = numericValue;
		this.updateModelFromThis();
	}

	// ----- reverse toggle -----

	@tracked isReverseOn = false;

	@action
	changeIsReverseOn(isOn: boolean): void {
		this.isReverseOn = isOn;
		this.updateModelFromThis();
	}

	// ----- secondary axis specifics -----

	@tracked isSecondaryAxisOn = false;

	get showMetricsOnSecondaryAxis(): boolean {
		return this.isSecondaryYAxis && this.isSecondaryAxisOn;
	}

	get selectedDsOnSecondAxis(): number[] {
		return this.visualization.dataSelectsOnSecondAxis;
	}

	get selectedDsOnSecondAxisString(): string[] {
		return this.selectedDsOnSecondAxis.map((id) => String(id));
	}

	get metricOptions(): LabelValuePairString[] {
		return this.blockSetup.sortedDataSelectsShown.map((ds, idx) => {
			const metricName = ds.metricSetup.metric?.name;
			return {
				value: ds.id!,
				label: metricName || `Metric ${idx}`,
			};
		});
	}

	@action
	changeIsSecondaryAxisOn(isOn: boolean): void {
		this.isSecondaryAxisOn = isOn;

		if (isOn) {
			// if we have more metrics, automatically add the last metric to this axis
			const selectedDsOnSecondAxis = this.selectedDsOnSecondAxis;
			if (!selectedDsOnSecondAxis.length && this.metricOptions.length > 1) {
				const dataSelect = this.blockSetup.sortedDataSelectsShown
					.filter((ds) => {
						return !selectedDsOnSecondAxis.includes(ds.numericId!);
					})
					.slice(-1)[0];
				if (dataSelect) {
					this.changeDsOnSecondAxis([dataSelect.id!]);
				}
			}
		} else {
			this.changeDsOnSecondAxis([]);
		}

		this.blockSetup.trackBlockPropertySettingsChangeEvent({ add_2nd_y_axis: isOn });
	}

	@action
	changeDsOnSecondAxis(dsIds: string[]): void {
		this.visualization.dataSelectsOnSecondAxis = dsIds.map((idString) => +idString);
		this.blockSetup.dataSelectsShown.forEach((ds) => {
			this.blockSetup.saveDataSelect(ds);
		});
	}

	// ----- updating to / from model -----

	updateModelFromThis(scaleType = this.selectedScaleType): void {
		this.updateYAxisScale(scaleType, this.minValue, this.maxValue, this.isReverseOn);
	}

	@action
	updateThisFromModel(): void {
		const formula = this.yAxisScaleSettings as YAxisScaleNotOff | undefined;
		this.minValue = formula?.min ?? null;
		this.maxValue = formula?.max ?? null;
		this.isReverseOn = !!formula?.reverse;

		this.isSecondaryAxisOn = this.selectedDsOnSecondAxis.length > 0;

		this.showValueInputs = this.selectedScaleType === YAxisScaleType.CUSTOM;
	}

	private updateYAxisScale(
		scaleType: YAxisScaleType,
		min: number | null,
		max: number | null,
		reverse: boolean,
	): void {
		let settings: YAxisScale | null = null;
		if (scaleType === YAxisScaleType.OFF) {
			settings = { disabled: true };
		} else if (scaleType === YAxisScaleType.AUTO) {
			settings = { min: null, max: null };
		} else {
			settings = { min, max };
		}
		if (reverse) {
			// only set if true
			settings.reverse = true;
		}

		const block = this.block;

		const yAxisScale = Array.from(block.yAxisScale);
		yAxisScale[this.args.scaleIndex] = settings;

		block.yAxisScale = yAxisScale;
		this.blockSetup.saveBlock();
	}
	<template>
		<span {{didUpdate this.updateThisFromModel @blockM}}></span>

		{{#if this.isSecondaryYAxis}}
			<div class="row row--toggle">
				<FormCheckboxSwitchComponent
					@name="secondAxis"
					@selected={{this.isSecondaryAxisOn}}
					@onChange={{this.changeIsSecondaryAxisOn}}
				/>
				<label>Add 2nd Y axis</label>
			</div>

		{{/if}}

		{{#if this.showScalingTypeSelector}}
			<div class="row">
				<label>Y-axis scaling</label>
				<FormRadiobuttonGroupComponent
					class="radiobutton-switcher"
					@selected={{this.selectedScaleType}}
					@options={{this.scaleTypeOptions}}
					@onChange={{this.changeScaleType}}
					@theme="dark-blue"
				/>
			</div>
		{{/if}}

		{{#if this.showValueInputs}}
			<div class="row row--y-axis-minmax">
				<div class="row--half">
					<FormInputComponent
						@loseFocusOnEnter={{true}}
						@value={{this.minValue}}
						@focusOut={{this.changeMinValue}}
						@hideFloatLabel={{true}}
						placeholder="Minimum"
						@theme="dark"
					/>
				</div>
				<div class="row--half">
					<FormInputComponent
						@loseFocusOnEnter={{true}}
						@value={{this.maxValue}}
						@focusOut={{this.changeMaxValue}}
						@hideFloatLabel={{true}}
						placeholder="Maximum"
						@theme="dark"
					/>
				</div>
			</div>
		{{/if}}

		{{#if this.showMetricsOnSecondaryAxis}}
			<div class="row">
				<label>Metrics on 2nd Y axis</label>
				<FormSelectComponent
					name="metric2ndYAxis"
					@selected={{this.selectedDsOnSecondAxisString}}
					@options={{this.metricOptions}}
					@onChange={{this.changeDsOnSecondAxis}}
					@placeholder="Select metrics"
					@isNumeric={{false}}
					@isRequired={{true}}
					@isMultiple={{true}}
					@theme="dark"
				/>
			</div>
		{{/if}}

		{{#if this.showReverseToggle}}
			<div class="row row--toggle">
				<FormCheckboxSwitchComponent
					@name="invert"
					@selected={{this.isReverseOn}}
					@onChange={{this.changeIsReverseOn}}
				/>
				<label>Inverted y-axis</label>
			</div>
		{{/if}}
	</template>
}
